import React from 'react';
import LogoMark from '../layout/LogoMark';
import { useTranslation } from '../../utils/i18n';

export default function UnlockScreen({
  unlockPassword,
  unlockError,
  pinEnabled,
  onPasswordChange,
  onSubmit,
  onLogout
}) {
  const { t } = useTranslation();
  const [inputMode, setInputMode] = React.useState('keyboard'); // 'keyboard' or 'keypad'

  const handleKeyPress = (num) => {
    onPasswordChange(unlockPassword + num);
  };

  const handleBackspace = () => {
    onPasswordChange(unlockPassword.slice(0, -1));
  };

  const handleClear = () => {
    onPasswordChange('');
  };

  // Generate password dots representation for PIN mode
  const maxDots = 8;
  const dots = [];
  for (let i = 0; i < maxDots; i++) {
    dots.push(i < unlockPassword.length);
  }

  const handleToggleMode = () => {
    setInputMode(prev => prev === 'keypad' ? 'keyboard' : 'keypad');
    onPasswordChange(''); // Clear current characters to reset state
  };

  return (
    <div className={`auth-wrapper unlock-wrapper ${unlockError ? 'unlock-shake' : ''}`}>
      <section className="unlock-story-panel">
        <LogoMark />
        <div className="auth-kicker">{t('local_key_vault') || 'Local Key Vault'}</div>
        <h1>{t('unlock_title')}</h1>
        <p>{t('unlock_desc')}</p>
        <div className="unlock-status-grid">
          <span>E2EE</span>
          <span>{t('local_keys_label') || 'Local Keys'}</span>
          <span>{t('session_lock') || 'Session Lock'}</span>
        </div>
      </section>

      <section className="auth-card glass-panel unlock-card">
        <div className="unlock-orbit" aria-hidden="true">
          <span />
          <span />
        </div>
        <div className="auth-header unlock-header">
          <LogoMark compact />
          <p>{t('unlock_title')}</p>
        </div>

        {/* VAULT GLOWING LOCK STATUS */}
        <div className={`vault-status-indicator ${unlockError ? 'error' : 'secured'}`}>
          <span className="vault-status-dot"></span>
          <span className="vault-status-text">
            {unlockError 
              ? (t('unlock_status_denied') || 'ACCESS DENIED / RETRY') 
              : unlockPassword 
                ? (t('unlock_status_decrypting') || 'DECRYPTING VAULT...') 
                : (t('unlock_status_secured') || 'VAULT SECURED / LOCKED')}
          </span>
        </div>

        {/* DUAL MODE TOGGLE SELECTION */}
        {pinEnabled && <div className="vault-mode-selector">
          <button 
            type="button" 
            className="btn-secondary compact-btn btn-mode-toggle"
            onClick={handleToggleMode}
          >
            {inputMode === 'keypad' 
              ? (t('unlock_keyboard_toggle') || '⌨️ Tastatur verwenden') 
              : (t('unlock_keypad_toggle') || '🔢 PIN-Pad verwenden')}
          </button>
        </div>}

        <form onSubmit={event => onSubmit(event, inputMode === 'keypad' ? 'pin' : 'password')}>
          <input
            type="text"
            name="username"
            autoComplete="username"
            style={{ display: 'none' }}
            readOnly
          />

          {inputMode === 'keypad' ? (
            <>
              {/* DOTS INDICATOR */}
              <div className="vault-dots-container">
                {dots.map((active, i) => (
                  <span key={i} className={`vault-dot ${active ? 'active' : ''}`} />
                ))}
                {unlockPassword.length > maxDots && (
                  <span className="vault-dot-more">+{unlockPassword.length - maxDots}</span>
                )}
              </div>

              {/* Hidden text field for keypad input binding */}
              <div className="form-group vault-input-group hidden">
                <input
                  type="password"
                  value={unlockPassword}
                  readOnly
                  required
                />
              </div>

              {/* FUTURISTIC NUMBER KEYPAD */}
              <div className="vault-keypad">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
                  <button
                    key={num}
                    type="button"
                    className="keypad-btn"
                    onClick={() => handleKeyPress(num)}
                  >
                    {num}
                  </button>
                ))}
                <button
                  type="button"
                  className="keypad-btn keypad-btn-action clear"
                  onClick={handleClear}
                  title="Clear"
                >
                  C
                </button>
                <button
                  type="button"
                  className="keypad-btn"
                  onClick={() => handleKeyPress(0)}
                >
                  0
                </button>
                <button
                  type="button"
                  className="keypad-btn keypad-btn-action backspace"
                  onClick={handleBackspace}
                  title="Backspace"
                >
                  ⌫
                </button>
              </div>
            </>
          ) : (
            /* STANDARD KEYBOARD TEXT INPUT MODE */
            <div className="form-group vault-input-group active">
              <label>{t('auth_password')}</label>
              <input
                type="password"
                className="input-field vault-password-input active"
                placeholder={t('auth_password_placeholder') || 'Passwort eingeben...'}
                value={unlockPassword}
                onChange={event => onPasswordChange(event.target.value)}
                autoComplete="current-password"
                autoFocus
                required
              />
            </div>
          )}

          {unlockError && <p className="form-error vault-error-message">{unlockError}</p>}
          
          <button type="submit" className="btn-primary btn-vault-unlock">
            🔓 {t('unlock_btn') || 'Tresor öffnen'}
          </button>
        </form>

        <button type="button" className="btn-secondary btn-vault-logout" onClick={onLogout}>
          {t('unlock_logout')}
        </button>
      </section>
    </div>
  );
}
